import { Controller, FormProvider, useForm } from "react-hook-form";

import { useMutation } from "@tanstack/react-query";

import { zodResolver } from "@hookform/resolvers/zod";

import { CheckIcon, KeyRoundIcon, LockIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Field, FieldError, FieldGroup, FieldLabel } from "@/components/ui/field";

import { PasswordInput } from "@/features/account/components/PasswordInput";
import { PASSWORD_REQUIREMENTS } from "@/features/account/config";
import { UpdatePasswordSchema, updatePasswordSchema } from "@/features/account/schemas";
import { updatePassword } from "@/features/account/services/account";
import Error from "@/shared/components/Error";
import Success from "@/shared/components/Success";
import { AppError } from "@/shared/lib/utils/errors";

const EMPTY_VALUES: UpdatePasswordSchema = {
  currentPassword: "",
  newPassword: "",
  confirmNewPassword: "",
};

export default function UpdatePasswordForm() {
  const form = useForm<UpdatePasswordSchema>({
    resolver: zodResolver(updatePasswordSchema),
    defaultValues: EMPTY_VALUES,
  });

  const watchedNewPassword = form.watch("newPassword");

  const mutation = useMutation<undefined, AppError | Error, UpdatePasswordSchema>({
    mutationFn: updatePassword,
    onSuccess: () => {
      form.reset(EMPTY_VALUES);
    },
  });

  async function onSubmit(values: UpdatePasswordSchema) {
    mutation.mutate(values);
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <LockIcon className="size-4 text-primary" />
          <CardTitle>Password</CardTitle>
        </div>
        <CardDescription>Choose a strong password that you don&rsquo;t use anywhere else.</CardDescription>
      </CardHeader>
      <CardContent>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            {mutation.isError && <Error title="Unable to update password" error={mutation.error} />}
            {mutation.isSuccess && !form.formState.isDirty && <Success title="Password updated" description="Your new password is active immediately." />}
            <FieldGroup>
              <Controller
                name="currentPassword"
                control={form.control}
                render={({ field, fieldState }) => (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={field.name}>Current password</FieldLabel>
                    <PasswordInput {...field} id={field.name} disabled={mutation.isPending} aria-invalid={fieldState.invalid} placeholder="••••••••" autoComplete="current-password" />
                    {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                  </Field>
                )}
              />
              <Controller
                name="newPassword"
                control={form.control}
                render={({ field, fieldState }) => (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={field.name}>New password</FieldLabel>
                    <PasswordInput {...field} id={field.name} disabled={mutation.isPending} aria-invalid={fieldState.invalid} placeholder="••••••••" autoComplete="new-password" />
                    <ul className="mt-1 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                      {PASSWORD_REQUIREMENTS.map((requirement) => {
                        const met = requirement.test(watchedNewPassword ?? "");

                        return (
                          <li key={requirement.label} className={`flex items-center gap-2 text-xs ${met ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`}>
                            <span className={`flex size-4 shrink-0 items-center justify-center ${met ? "bg-emerald-500/10" : "bg-muted"}`}>
                              <CheckIcon className={`size-3 ${met ? "opacity-100" : "opacity-30"}`} />
                            </span>
                            {requirement.label}
                          </li>
                        );
                      })}
                    </ul>
                    {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                  </Field>
                )}
              />
              <Controller
                name="confirmNewPassword"
                control={form.control}
                render={({ field, fieldState }) => (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={field.name}>Confirm new password</FieldLabel>
                    <PasswordInput {...field} id={field.name} disabled={mutation.isPending} aria-invalid={fieldState.invalid} placeholder="••••••••" autoComplete="new-password" />
                    {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                  </Field>
                )}
              />
              <div className="flex items-center justify-end gap-2">
                <Button type="button" variant="outline" size="sm" disabled={mutation.isPending || !form.formState.isDirty} onClick={() => form.reset(EMPTY_VALUES)}>
                  Cancel
                </Button>
                <Button size="sm" disabled={mutation.isPending || !form.formState.isDirty}>
                  <KeyRoundIcon />
                  {mutation.isPending ? "Updating..." : "Update password"}
                </Button>
              </div>
            </FieldGroup>
          </form>
        </FormProvider>
      </CardContent>
    </Card>
  );
}
